import { Grid } from '../../../../Common/Grid'
import { Button, IconButton, Input, Typography, Divider } from '@fullscreendigital/carrefour-dex-fe'
import { TitleHeader } from '../../../Common/TitleHeader'
import { getValidationSchema, initialValues } from './helpers'
import { ManageSearch as ManageSearchIcon } from '@mui/icons-material'
import { useQuery } from 'react-query'
import { makeCoreGetRequest, makeCorePostRequest } from '../../../../../../utils/api'
import { formatErrorMessage } from '../../../../../../utils/formatting'
import { useNotification } from '../../../../../../hooks'
import { useLocation, useNavigate } from 'react-router-dom'
import { Fragment, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import AlertModal from '../../../Common/AlertModal/AlertModal'
import { URLS } from '../../../constants'
import { CircularProgress } from '@mui/material'
import { validateScanCode } from '../../../helpers'
import { ScrollTopButton } from 'components/core-ui/ScrollTopButton'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'

const Step2 = () => {
  const { showNotification } = useNotification()
  const location = useLocation()
  const navigate = useNavigate()
  const id = location.state?.id || ''

  const [isInvalidSupportTypeModalOpen, setIsInvalidSupportTypeModalOpen] = useState(false)
  const [invalidSupportTypeModalContent, setInvalidSupportTypeModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [isReturnableModalOpen, setIsReturnableModalOpen] = useState(false)
  const [returnableModalContent, setReturnableModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [isSupportScanModalOpen, setIsSupportScanModalOpen] = useState(false)
  const [supportScanModalContent, setSupportScanModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [packageScanned, setPackageScanned] = useState(() => {
    const saved = localStorage.getItem(`packageScanned-${id}`)
    return saved !== null ? JSON.parse(saved) : false
  })
  const [searchCode, setSearchCode] = useState('')

  const validationSchema = useMemo(() => getValidationSchema(), [])
  const formik = useFormik({
    enableReinitialize: false,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values: any) => {
      if (values.returnable === false) {
        showReturnableModal()
      } else {
        try {
          await makeCorePostRequest(
            '/shop-reception-mobile/truck/shop/exit/support-type/save/' + id,
            { supportTypeId: values.currentSupportTypeId, supportNumber: values.supportNumber }
          )

          setPackageScanned(true)
          localStorage.setItem(`packageScanned-${id}`, JSON.stringify(true))
          setSearchCode('')
          document.getElementById('scan-input').focus()
          await formik.setFieldValue('supportType', '')
          await formik.setFieldValue('currentSupportTypeId', '')
        } catch (error) {
          showNotification({
            message: `Error while submitting. ${formatErrorMessage(error)}`,
            type: 'error'
          })
        }
      }
    }
  })

  const { data: returnTruckModel, isLoading: isLoadingReturnTruck } = useQuery(
    'returnTruckModel',
    async () => {
      return await makeCoreGetRequest(
        '/shop-reception-mobile/truck/shop/exit/truck-model/' + id,
        null
      )
    },
    {
      onError: error => {
        showNotification({
          message: `Error while fetching truck. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { data: supportList, isLoading: isLoadingSupports } = useQuery(
    'supportList',
    async () => {
      return await makeCoreGetRequest('/shop-reception-mobile/support/shop/all', null)
    },
    {
      onError: error => {
        showNotification({
          message: `Error while fetching supports. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  if (isLoadingReturnTruck || isLoadingSupports) {
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
  }

  const showInvalidSupportTypeModal = () => {
    setInvalidSupportTypeModalContent({
      title: 'Atenție!',
      description: 'Acest ambalaj nu este de tip CHEP!',
      buttons: [
        {
          label: 'Ok',
          onClick: () => {
            setIsInvalidSupportTypeModalOpen(false)
          },
          color: 'primary'
        }
      ]
    })
    setIsInvalidSupportTypeModalOpen(true)
  }

  const closeInvalidSupportTypeModal = () => {
    setIsInvalidSupportTypeModalOpen(false)
  }

  const showReturnableModal = () => {
    setReturnableModalContent({
      title: 'Atenție!',
      description:
        'Ambalajul introdus este nereturnabil. Va rugam sa introduceti un tip de ambalaj returnabil.',
      buttons: [
        {
          label: 'Ok',
          onClick: () => {
            setIsReturnableModalOpen(false)
          },
          color: 'primary'
        }
      ]
    })
    setIsReturnableModalOpen(true)
  }

  const closeReturnableModal = () => {
    setIsReturnableModalOpen(false)
  }

  const showSupportScanModal = () => {
    setSupportScanModalContent({
      title: 'Atenție!',
      description: 'Cod de ambalaj necunoscut!',
      buttons: [
        {
          label: 'Ok',
          onClick: () => {
            setSearchCode('')
            setIsSupportScanModalOpen(false)
          },
          color: 'primary'
        }
      ]
    })
    setIsSupportScanModalOpen(true)
  }

  const closeSupportScanModal = () => {
    setIsSupportScanModalOpen(false)
  }

  const supportScan = () => {
    if (!searchCode) return

    if (!validateScanCode(showNotification, searchCode, 'suport')) return

    try {
      makeCorePostRequest(
        '/shop-reception-mobile/truck/shop/exit/support-type/scan/' + id,
        searchCode,
        {
          headers: {
            'Content-Type': 'text/plain'
          }
        }
      ).then(r => {
        if (!r.scanSuccess) {
          showSupportScanModal()
        } else if (
          returnTruckModel.depositOrHubName.includes('CHEP') &&
          !r.supportType.includes('CHEP')
        ) {
          showInvalidSupportTypeModal()
        } else {
          formik.setValues({
            supportType: r.supportType,
            supportNumber: r.supportNumber,
            currentSupportTypeId: r.supportTypeId,
            returnable: r.returnable
          })
        }
      })
    } catch (error) {
      showNotification({
        message: `Error while scanning support. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const handleSupportTypeChange = selectedValue => {
    if (selectedValue) {
      setSearchCode('')
      const selectedSupport = supportList.find(support => support.id === selectedValue)

      if (
        returnTruckModel.depositOrHubName.includes('CHEP') &&
        !selectedSupport.name.includes('CHEP')
      ) {
        showInvalidSupportTypeModal()
      } else {
        formik.setValues({
          supportType: selectedSupport.name,
          supportNumber: selectedSupport.conform,
          currentSupportTypeId: selectedSupport.id,
          returnable: selectedSupport.returnable
        })
      }
    }
  }

  let items = [
    <TitleHeader title="ÎNREGISTREAZǍ CAMION" />,
    <>
      <Typography variant="medium" component="p">
        Numǎr camion: {returnTruckModel?.truckNumber}
      </Typography>
      <Typography variant="medium" component="p">
        Destinație: {returnTruckModel?.depositOrHubName}
      </Typography>
    </>,
    <div style={{ display: 'flex' }}>
      <Input
        type="text"
        label="Scanează tip ambalaj"
        variant="standard"
        autoFocus
        value={searchCode}
        onChange={event => setSearchCode((event.target as HTMLInputElement).value)}
        onKeyUp={event => {
          const target = event.target as HTMLInputElement
          if (event.which == 0) {
            if (target.value) {
              setSearchCode((target as HTMLInputElement).value)
              supportScan()
            }
          }
        }}
        width="100%"
        id="scan-input"
      />
      <IconButton onClick={supportScan}>
        <ManageSearchIcon />
      </IconButton>
    </div>,
    <AlertModal
      open={isSupportScanModalOpen}
      handleClose={closeSupportScanModal}
      title={supportScanModalContent.title}
      description={supportScanModalContent.description}
      buttons={supportScanModalContent.buttons}
    />,
    <AsyncAutocomplete
      variant="outlined"
      name="searchSupportType"
      label="Cauta tip suport"
      options={supportList?.map(support => ({ value: support.id, label: support.name }))}
      onChange={support => handleSupportTypeChange(support?.value)}
      error={!!formik.errors.supportType}
      helperText={formik.errors.supportType as string}
    />,
    <AlertModal
      open={isInvalidSupportTypeModalOpen}
      handleClose={closeInvalidSupportTypeModal}
      title={invalidSupportTypeModalContent.title}
      description={invalidSupportTypeModalContent.description}
      buttons={invalidSupportTypeModalContent.buttons}
    />,
    <Divider />,
    formik.values.currentSupportTypeId && (
      <Fragment>
        <Input
          type="text"
          label="Tip ambalaj"
          variant="outlined"
          width="100%"
          value={formik.values.supportType}
          style={{ marginBottom: '15px' }}
          readonly
        />
        <Input
          type="number"
          label="Număr ambalaje"
          variant="outlined"
          width="100%"
          value={formik.values.supportNumber}
          onChange={event =>
            formik.setFieldValue('supportNumber', +(event.target as HTMLInputElement).value)
          }
          error={!!formik.errors.supportNumber}
          helperText={formik.errors.supportNumber as string}
        />
        <Button
          type="button"
          variant="contained"
          label="PASUL URMǍTOR"
          onClick={() => formik.handleSubmit()}
          disabled={formik.isSubmitting}
          style={{ width: '100%', marginTop: '15px' }}
        />
        <AlertModal
          open={isReturnableModalOpen}
          handleClose={closeReturnableModal}
          title={returnableModalContent.title}
          description={returnableModalContent.description}
          buttons={returnableModalContent.buttons}
        />
      </Fragment>
    ),
    !formik.values.currentSupportTypeId && (
      <Button
        type="button"
        variant="contained"
        label="PASUL URMǍTOR - SUMAR"
        onClick={() => navigate(URLS.AUTH_RETURN_STEP_3, { state: id })}
        disabled={!packageScanned}
        style={{ width: '100%' }}
      />
    ),
    <Button
      type="button"
      variant="contained"
      label="Modifica Date Camion"
      onClick={() => navigate('/shop-reception/mobile/return-step-1', { state: { truckId: id } })}
      style={{ width: '100%', marginTop: '15px' }}
    />,
    <ScrollTopButton />
  ]

  return (
    <Grid gridContainerStyle={{ marginTop: '20px' }} maxWidth="xs" items={items} itemsPerRow={1} />
  )
}

export default Step2
